import { useQuery } from '@tanstack/react-query';
import { useUserStore } from '@/store/userStore';
import { getUpcomingClasses } from '@/services/edgeFunctionWrappers/dashboardStatsWrapper';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, Clock, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function UpcomingClassesWidget() {
  const { gymId } = useUserStore();

  const { data, isLoading } = useQuery({
    queryKey: ['upcoming-classes', gymId],
    queryFn: () => getUpcomingClasses(gymId!),
    enabled: !!gymId,
    staleTime: 2 * 60 * 1000, // 2 minutes - more frequent refresh for upcoming classes
    refetchInterval: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            Prochains cours
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-24">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const upcomingClasses = data || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          Prochains cours
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {upcomingClasses.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Aucun cours à venir aujourd'hui
          </p>
        ) : (
          <ul className="space-y-2">
            {upcomingClasses.map((cls) => (
              <li 
                key={cls.id} 
                className="flex items-center justify-between py-1.5 border-b last:border-0"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{cls.name}</span>
                  <span className="text-xs text-muted-foreground">{cls.instructor}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>{cls.time}</span>
                  <span className={cn(
                    "font-medium",
                    cls.spotsLeft <= 2 ? "text-destructive" : "text-primary"
                  )}>
                    {cls.spotsLeft} place{cls.spotsLeft !== 1 ? 's' : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
